import type { ReactNode } from "react";

import type { ProtectedItemResponse, SurgeProtectionResponse } from "../services/surgeProtection";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";
import {
  BACKUP_DEVICE_KIND_LABELS,
  CONNECTION_MODE_LABELS,
  CONTINUOUS_VOLTAGE_BASIS_LABELS,
  COORDINATION_LABELS,
  EARTHING_SYSTEM_LABELS,
  LOCATION_LABELS,
  LPS_LABELS,
  PROSPECTIVE_CURRENT_BASIS_LABELS,
  PROTECTION_LEVEL_BASIS_LABELS,
  SPD_TYPE_LABELS,
  UP_BASIS_LABELS,
} from "./surgeProtectionLabels";
import { STATUS_LABELS, itemKind, statusHeading } from "./SurgeProtectionResultSummary";

interface SurgeProtectionResultPanelProps {
  result: SurgeProtectionResponse;
}

/** Carried by every result (decisions D3-D8, D10): what the check is and what it is not. */
export const METHOD_NOTE =
  "The effective protection level is the declared Up plus the voltage drop across the declared " +
  "connecting leads, compared with each protected item's declared rated impulse withstand " +
  "voltage; Uc, UT and the discharge parameters are compared with declared requirements; the " +
  "assembly's short-circuit rating is compared with the prospective current of the linked fault " +
  "run or the declaration. Whether an SPD is required, the lightning protection zones, the " +
  "back-up device's own rating, the earth lead, the status indication and the coordination with " +
  "every other SPD are not checked here; the sheet says so on every study. No standard's value is " +
  "applied: every requirement is the engineer's or the manufacturer's declared figure with its " +
  "source.";

// The three ratios of the result read in two directions (register follow-up 41): Uc / required
// minimum is capacity / requirement and passes at or above 1; Up_eff / Uw per item and
// Ik'' / rating are demand / capacity and pass at or below 1. The captions say so wherever a
// ratio is printed, on screen and in the EOS-13 report, which renders this panel.
export const UC_RATIO_CAPTION = "Uc − required minimum · Uc / required minimum (≥ 1 passes)";
export const ISCCR_RATIO_CAPTION = "rating − Ik'' · Ik'' / rating: demand / capacity (≤ 1 passes)";
export const ITEM_RATIO_CAPTION = "Up_eff / Uw of the item: demand / capacity (≤ 1 passes)";

export const RESULT_WORDS = {
  met: "Met",
  notMet: "Not met",
  notCompared: "Not compared",
} as const;

function resultKey(met: boolean | null): string {
  if (met === null) {
    return "not-evaluated";
  }
  return met ? "pass" : "fail";
}

function resultWord(met: boolean | null): string {
  if (met === null) {
    return RESULT_WORDS.notCompared;
  }
  return met ? RESULT_WORDS.met : RESULT_WORDS.notMet;
}

// One cell holding an engineering figure: four significant figures with the unit on screen,
// the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null | undefined; unit?: string }) {
  const quantity = formatQuantity(value ?? null, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function Verdict({ met }: { met: boolean | null }) {
  const key = resultKey(met);
  return (
    <td data-check-result={key}>
      <span data-check-pill>
        <StatusIcon tone={checkTone(key)} size={16} />
        {resultWord(met)}
      </span>
    </td>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
              <th scope="col">Basis</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

function sourceOr(source: string | null | undefined): string {
  return source ?? "source not declared";
}

type Declared = { value: string; source: string | null } | null;

// A declared figure with its source as the basis; "not declared" when none.
function DeclaredRow({
  rowKey,
  label,
  declared,
  unit,
  basisWhenMissing = "not declared",
}: {
  rowKey: string;
  label: string;
  declared: Declared;
  unit?: string;
  basisWhenMissing?: string;
}) {
  return (
    <tr data-row-key={rowKey}>
      <th scope="row">{label}</th>
      <Quantity value={declared?.value ?? null} unit={unit} />
      <td>{declared === null ? basisWhenMissing : sourceOr(declared.source)}</td>
    </tr>
  );
}

// The location as declared: the SPD, its type, the system, the earthing and the mode, the LPS.
function LocationTable({ result }: { result: SurgeProtectionResponse }) {
  return (
    <ValueTable title="SPD location">
      <tr data-row-key="spd_type">
        <th scope="row">SPD</th>
        <td data-spd-type={result.spd.spd_type}>
          {result.spd_code} · {SPD_TYPE_LABELS[result.spd.spd_type]}
        </td>
        <td data-location-kind={result.location_kind}>{LOCATION_LABELS[result.location_kind]}</td>
      </tr>
      <tr data-row-key="system_voltage_v">
        <th scope="row">System voltage (V)</th>
        <Quantity value={result.system_voltage_v.value} unit="V" />
        <td>{sourceOr(result.system_voltage_v.source)}</td>
      </tr>
      <tr data-row-key="earthing">
        <th scope="row">Earthing system · connection mode</th>
        <td data-earthing-system={result.earthing_system} data-connection-mode={result.connection_mode}>
          {EARTHING_SYSTEM_LABELS[result.earthing_system]} · {CONNECTION_MODE_LABELS[result.connection_mode]}
        </td>
        <td>{sourceOr(result.connection_mode_source)} · declared, not validated against the system</td>
      </tr>
      <tr data-row-key="lps_presence">
        <th scope="row">Lightning protection system</th>
        <td data-lps-presence={result.lps_presence}>{LPS_LABELS[result.lps_presence]}</td>
        <td>declared (EOS-09 not built)</td>
      </tr>
      <tr data-row-key="bus">
        <th scope="row">Bus (SLD) · other SPDs</th>
        <td>{result.sld_bus_code ?? EMPTY_QUANTITY}</td>
        <td>
          {result.upstream_spd_code === null && result.downstream_spd_code === null
            ? "no upstream or downstream SPD named"
            : [
                result.upstream_spd_code === null ? null : `upstream ${result.upstream_spd_code}`,
                result.downstream_spd_code === null ? null : `downstream ${result.downstream_spd_code}`,
              ]
                .filter((word): word is string => word !== null)
                .join(" · ")}
        </td>
      </tr>
    </ValueTable>
  );
}

// The protection level (3.1): Up, the lead drop and its basis, Up_eff, the lowest Uw, the verdict.
function ProtectionLevelTable({ result }: { result: SurgeProtectionResponse }) {
  const leads = result.leads;
  return (
    <ValueTable title="Protection level">
      <tr data-row-key="up_kv">
        <th scope="row">Voltage protection level Up (kV)</th>
        <Quantity value={result.spd.up_kv.value} unit="kV" />
        <td data-up-basis={result.spd.up_basis}>
          {sourceOr(result.spd.up_kv.source)} · {UP_BASIS_LABELS[result.spd.up_basis]}
        </td>
      </tr>
      <tr data-row-key="du_leads_kv">
        <th scope="row">Lead voltage drop dU (kV)</th>
        <Quantity value={result.du_leads_kv} unit="kV" />
        <td data-protection-level-basis={result.protection_level_basis}>
          {leads === null
            ? PROTECTION_LEVEL_BASIS_LABELS[result.protection_level_basis]
            : result.protection_level_basis === "DECLARED_LEAD_VOLTAGE"
              ? `declared ${sourceOr(leads.lead_voltage_kv?.source)}`
              : `${formatQuantity(leads.lead_length_m?.value ?? null, "m").display} × ${formatQuantity(
                  leads.lead_inductive_drop_kv_per_m?.value ?? null,
                  "kV/m",
                ).display} · ${sourceOr(leads.lead_inductive_drop_kv_per_m?.source)}`}
        </td>
      </tr>
      <tr data-row-key="up_eff_kv">
        <th scope="row">Effective protection level Up_eff (kV)</th>
        <Quantity value={result.up_eff_kv} unit="kV" />
        <td>Up + dU</td>
      </tr>
      <tr data-row-key="lowest_uw_kv">
        <th scope="row">Lowest Uw of the items (kV)</th>
        <Quantity value={result.lowest_uw_kv} unit="kV" />
        <td>the smallest declared rated impulse withstand voltage</td>
      </tr>
      <tr data-row-key="protection_level_margin_kv">
        <th scope="row">Protection-level margin (kV)</th>
        <Quantity value={result.protection_level_margin_kv} unit="kV" />
        <td>lowest Uw − Up_eff (negative when exceeded)</td>
      </tr>
      <tr data-row-key="protection_level_result">
        <th scope="row">Protection-level condition</th>
        <Verdict met={result.protection_level_met} />
        <td>{result.protection_level_met === null ? "no leads declared: nothing compared" : "every item Up_eff ≤ Uw"}</td>
      </tr>
    </ValueTable>
  );
}

// Uc and UT (3.2) against their declared requirements.
function ContinuousVoltageTable({ result }: { result: SurgeProtectionResponse }) {
  const spd = result.spd;
  const required = result.requirements;
  return (
    <ValueTable title="Continuous operating voltage">
      <DeclaredRow rowKey="uc_v" label="Continuous operating voltage Uc (V)" declared={spd.uc_v} unit="V" />
      <DeclaredRow rowKey="uc_minimum_v" label="Minimum Uc declared (V)" declared={required.uc_minimum_v} unit="V" />
      <tr data-row-key="uc_margin">
        <th scope="row">Uc margin (V) · Uc / required minimum</th>
        <td title={`${result.uc_margin_v ?? "—"} · ${result.uc_ratio ?? "—"}`}>
          {formatQuantity(result.uc_margin_v).display} · {formatQuantity(result.uc_ratio).display}
        </td>
        <td>{UC_RATIO_CAPTION}</td>
      </tr>
      <tr data-row-key="uc_result">
        <th scope="row">Uc condition</th>
        <Verdict met={result.uc_met} />
        <td data-continuous-voltage-basis={result.continuous_voltage_basis}>
          {CONTINUOUS_VOLTAGE_BASIS_LABELS[result.continuous_voltage_basis]}
        </td>
      </tr>
      <DeclaredRow rowKey="ut_v" label="TOV withstand UT (V)" declared={spd.ut_v} unit="V" />
      <DeclaredRow rowKey="ut_required_v" label="Required UT (V)" declared={required.ut_required_v} unit="V" />
      <tr data-row-key="ut_result">
        <th scope="row">UT condition</th>
        <Verdict met={result.ut_met} />
        <td>{result.ut_met === null ? "UT or its requirement not declared" : "UT ≥ requirement"}</td>
      </tr>
    </ValueTable>
  );
}

// The discharge parameters (3.3) the SPD type carries against their declared requirements.
function DischargeTable({ result }: { result: SurgeProtectionResponse }) {
  const spd = result.spd;
  const required = result.requirements;
  const rows: {
    key: string;
    label: string;
    declared: Declared;
    requirement: Declared;
    margin: string | null;
    met: boolean | null;
  }[] = [
    { key: "iimp", label: "Impulse current Iimp (kA)", declared: spd.iimp_ka, requirement: required.iimp_required_ka, margin: result.iimp_margin_ka, met: result.iimp_met },
    { key: "in", label: "Nominal discharge current In (kA)", declared: spd.in_ka, requirement: required.in_required_ka, margin: result.in_margin_ka, met: result.in_met },
    { key: "imax", label: "Maximum discharge current Imax (kA)", declared: spd.imax_ka, requirement: required.imax_required_ka, margin: result.imax_margin_ka, met: result.imax_met },
  ];
  return (
    <section aria-label="Discharge parameters">
      <h3>Discharge parameters</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">SPD</th>
              <th scope="col">Required</th>
              <th scope="col">Margin (kA)</th>
              <th scope="col">Condition</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} data-row-key={`${row.key}_ka`}>
                <th scope="row">{row.label}</th>
                <td title={row.declared === null ? undefined : `${row.declared.value} kA · ${sourceOr(row.declared.source)}`}>
                  {row.declared === null ? "not carried / not declared" : formatQuantity(row.declared.value, "kA").display}
                </td>
                <td title={row.requirement === null ? undefined : `${row.requirement.value} kA · ${sourceOr(row.requirement.source)}`}>
                  {row.requirement === null ? "not declared" : formatQuantity(row.requirement.value, "kA").display}
                </td>
                <Quantity value={row.margin} unit="kA" />
                <Verdict met={row.met} />
              </tr>
            ))}
            <tr data-row-key="iimp_per_mode">
              <th scope="row">Iimp per mode</th>
              <td colSpan={4}>
                {spd.iimp_per_mode_declared
                  ? "declared per mode by the engineer; the split of a set value is not computed"
                  : "as the manufacturer states it"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

// The short-circuit withstand (3.4): the prospective current and its basis against the rating.
function ShortCircuitTable({ result }: { result: SurgeProtectionResponse }) {
  const current = result.prospective_current;
  const run = current?.linked_fault_run ?? null;
  const device = result.spd.backup_device;
  return (
    <ValueTable title="Short-circuit withstand">
      <tr data-row-key="prospective_current_ka">
        <th scope="row">Prospective current Ik'' at the location (kA)</th>
        <Quantity value={result.prospective_current_ka} unit="kA" />
        <td data-prospective-current-basis={result.prospective_current_basis}>
          {run !== null
            ? `${run.study_code} rev ${run.revision_number} · bus ${run.fault_bus_code} · ${run.calculation_case.toLowerCase()} ${run.fault_type.toLowerCase().replaceAll("_", " ")}`
            : current !== null
              ? `${PROSPECTIVE_CURRENT_BASIS_LABELS[result.prospective_current_basis]} · ${sourceOr(current.source_text)}`
              : PROSPECTIVE_CURRENT_BASIS_LABELS[result.prospective_current_basis]}
        </td>
      </tr>
      <DeclaredRow rowKey="isccr_ka" label="Short-circuit current rating of the assembly (kA)" declared={result.spd.isccr_ka} unit="kA" />
      <tr data-row-key="backup_device">
        <th scope="row">Back-up device</th>
        <td title={device === null ? undefined : `${device.rating_a} A`}>
          {device === null
            ? EMPTY_QUANTITY
            : `${BACKUP_DEVICE_KIND_LABELS[device.kind]} ${formatQuantity(device.rating_a, "A").display}${
                device.sld_device_code === null ? "" : ` · ${device.sld_device_code}`
              }`}
        </td>
        <td>{device === null ? "not declared" : sourceOr(device.source)}</td>
      </tr>
      <tr data-row-key="isccr_margin">
        <th scope="row">Rating margin (kA) · Ik'' / rating</th>
        <td title={`${result.isccr_margin_ka ?? "—"} · ${result.isccr_ratio ?? "—"}`}>
          {formatQuantity(result.isccr_margin_ka).display} · {formatQuantity(result.isccr_ratio).display}
        </td>
        <td>{ISCCR_RATIO_CAPTION}</td>
      </tr>
      <tr data-row-key="isccr_result">
        <th scope="row">Short-circuit condition</th>
        <Verdict met={result.isccr_met} />
        <td>{result.isccr_met === null ? "the rating or the current is not established" : "rating ≥ Ik''"}</td>
      </tr>
    </ValueTable>
  );
}

// The protection distance and the energy coordination (3.5) as declared.
function CoordinationTable({ result }: { result: SurgeProtectionResponse }) {
  const spd = result.spd;
  return (
    <ValueTable title="Distance and coordination">
      <DeclaredRow
        rowKey="protection_distance_m"
        label="Protection distance declared (m)"
        declared={spd.protection_distance_m}
        unit="m"
        basisWhenMissing="not declared (the items' distances were not checked)"
      />
      <tr data-row-key="coordination">
        <th scope="row">Energy coordination</th>
        <td data-coordination={spd.coordination}>{COORDINATION_LABELS[spd.coordination]}</td>
        <td>
          {spd.coordination === "DECLARED_BY_MANUFACTURER"
            ? `${sourceOr(spd.coordination_source)}${spd.coordinated_spd_reference === null ? "" : ` · with ${spd.coordinated_spd_reference}`}`
            : "not declared by the manufacturer"}
        </td>
      </tr>
    </ValueTable>
  );
}

// One line per protected item: Uw, the distance, the comparisons, the status.
function ItemsTable({ items }: { items: readonly ProtectedItemResponse[] }) {
  return (
    <section aria-label="Protected items">
      <h3>Protected items</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Uw (kV)</th>
              <th scope="col">Margin Uw − Up_eff (kV)</th>
              <th scope="col" title={ITEM_RATIO_CAPTION}>
                Up_eff / Uw (≤ 1 passes)
              </th>
              <th scope="col">Protection level</th>
              <th scope="col">Distance (m)</th>
              <th scope="col">Within protection distance</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.code} data-protected-item={item.code} data-item-status={item.status} data-item-kind={itemKind(item)}>
                <th scope="row" title={item.name}>
                  {item.code}
                </th>
                <td title={`${item.uw_kv} kV · ${sourceOr(item.uw_source)}`}>{formatQuantity(item.uw_kv, "kV").display}</td>
                <Quantity value={item.margin_kv} unit="kV" />
                <Quantity value={item.ratio} />
                <Verdict met={item.met} />
                <td title={item.distance_excess_m === null ? undefined : `excess ${item.distance_excess_m} m`}>
                  {item.distance_m === null ? "not declared" : formatQuantity(item.distance_m, "m").display}
                </td>
                <Verdict met={item.within_protection_distance} />
                <td data-check-result={item.status === "DESIGN_CHECK_PASSED" ? "pass" : item.status === "NO_SOLUTION" ? "fail" : "review"}>
                  <span data-check-pill>
                    <StatusIcon tone={checkTone(item.status === "DESIGN_CHECK_PASSED" ? "pass" : item.status === "NO_SOLUTION" ? "fail" : "review")} size={16} />
                    {STATUS_LABELS[item.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function SurgeProtectionResultPanel({ result }: SurgeProtectionResultPanelProps) {
  return (
    <article aria-label="Surge protection result">
      <header>
        <h2>Surge protection result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status: <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <LocationTable result={result} />
      <ProtectionLevelTable result={result} />
      <ContinuousVoltageTable result={result} />
      <DischargeTable result={result} />
      <ShortCircuitTable result={result} />
      <CoordinationTable result={result} />
      <ItemsTable items={result.items} />

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. No profile holds an SPD table in
          this version: Uw, the minimum Uc, the lead drop, the discharge requirements and the
          protection distance are the engineer's or the manufacturer's declared figures with their
          sources (GAP-029); the arithmetic is universal.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the SPD manufacturer's data, the
          equipment data sheets and every declared figure and its source before approval or issue.{" "}
          {METHOD_NOTE}
        </p>
      </footer>
    </article>
  );
}

export default SurgeProtectionResultPanel;
